import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeftRight, TrendingUp, TrendingDown, Download, Mail, Loader2 } from "lucide-react";
import api, { inr, fmtDate } from "./api";
import { PageHeader, EmptyState } from "./components";
import { EASE } from "@/components/Motion";

export default function TransactionsPage() {
  const { user } = useOutletContext();
  const [txns, setTxns] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [mailBusy, setMailBusy] = useState(false);

  useEffect(() => {
    api.get("/transactions").then(({ data }) => setTxns(data)).catch(() => setTxns([]));
  }, []);

  const downloadPdf = async (kind = "full") => {
    setPdfBusy(kind);
    try {
      const res = await api.get("/statement/pdf", { params: { kind }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const dispo = res.headers?.["content-disposition"] || "";
      const match = dispo.match(/filename=([^;]+)/);
      a.download = match ? match[1].trim() : "beatkingsmm-statement.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${kind === "orders" ? "Orders" : kind === "transactions" ? "Transactions" : "Full"} statement downloaded`);
    } catch {
      toast.error("Could not generate the statement. Try again.");
    } finally {
      setPdfBusy(null);
    }
  };

  const emailStatement = async () => {
    setMailBusy(true);
    try {
      const { data } = await api.post("/statement/email");
      toast.success(`Statement on its way to ${data.queued_to}`);
    } catch {
      toast.error("Could not send the statement. Try again.");
    } finally {
      setMailBusy(false);
    }
  };

  return (
    <div data-testid="transactions-page">
      <PageHeader title="Transactions" sub="Every rupee in, every rupee out — accounted." />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: EASE }}
        className="mb-6 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-gold/25 bg-gradient-to-br from-gold/[0.06] to-transparent p-6"
        data-testid="statement-card"
      >
        <div>
          <h3 className="flex items-center gap-2 font-semibold text-neutral-100">
            <Download size={15} className="text-gold" /> Account statement
          </h3>
          <p className="mt-1 text-sm text-neutral-500">
            Download orders and transactions together — or as separate PDFs — or get the full statement in your inbox.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={emailStatement}
            disabled={!!mailBusy || !!pdfBusy}
            data-testid="statement-email-button"
            className="flex items-center gap-2 rounded-full border border-gold/40 px-5 py-2.5 text-xs font-semibold text-goldlight transition-all duration-300 hover:bg-gold hover:text-black disabled:opacity-40"
          >
            {mailBusy ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />}
            Email my statement
          </button>
          <button
            onClick={() => downloadPdf("orders")}
            disabled={!!pdfBusy}
            data-testid="statement-pdf-orders-button"
            className="flex items-center gap-2 rounded-full border border-gold/40 px-5 py-2.5 text-xs font-semibold text-goldlight transition-all duration-300 hover:bg-gold hover:text-black disabled:opacity-40"
          >
            {pdfBusy === "orders" ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
            Orders PDF
          </button>
          <button
            onClick={() => downloadPdf("transactions")}
            disabled={!!pdfBusy}
            data-testid="statement-pdf-transactions-button"
            className="flex items-center gap-2 rounded-full border border-gold/40 px-5 py-2.5 text-xs font-semibold text-goldlight transition-all duration-300 hover:bg-gold hover:text-black disabled:opacity-40"
          >
            {pdfBusy === "transactions" ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
            Transactions PDF
          </button>
          <button
            onClick={() => downloadPdf("full")}
            disabled={!!pdfBusy}
            data-testid="statement-pdf-button"
            className="flex items-center gap-2 rounded-full bg-gradient-to-br from-goldlight via-gold to-golddark px-5 py-2.5 text-xs font-bold text-black transition-shadow duration-300 hover:shadow-[0_8px_30px_rgb(var(--gold)_/_0.35)] disabled:opacity-40"
          >
            {pdfBusy === "full" ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
            Full PDF
          </button>
        </div>
      </motion.div>

      {txns === null ? (
        <div className="flex justify-center py-24">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-gold/20 border-t-gold" />
        </div>
      ) : txns.length === 0 ? (
        <EmptyState icon={ArrowLeftRight} title="No transactions yet" sub="Add funds to get the ledger moving." testid="transactions-empty" />
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="overflow-x-auto rounded-2xl border border-white/10"
          data-testid="transactions-table"
        >
          <div className="min-w-[760px]">
            <div className="grid grid-cols-12 gap-3 border-b border-white/10 bg-white/[0.02] px-5 py-3 font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-500">
              <span className="col-span-2">Txn ID</span>
              <span className="col-span-1">Type</span>
              <span className="col-span-2">Amount</span>
              <span className="col-span-2">Method</span>
              <span className="col-span-3">Note</span>
              <span className="col-span-1">Balance</span>
              <span className="col-span-1 text-right">Date</span>
            </div>
            {txns.map((t) => (
              <div
                key={t.id}
                data-testid={`txn-row-${t.txn_id}`}
                className="grid grid-cols-12 items-center gap-3 border-b border-white/5 bg-panel/80 px-5 py-3.5 transition-colors duration-300 last:border-0 hover:bg-gold/[0.04]"
              >
                <span className="col-span-2 font-mono text-xs text-neutral-400">{t.txn_id}</span>
                <span className="col-span-1">
                  <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ${
                    t.type === "credit"
                      ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-300"
                      : "border-rose-400/30 bg-rose-400/10 text-rose-300"
                  }`}>
                    {t.type === "credit" ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                    {t.type}
                  </span>
                </span>
                <span className={`col-span-2 text-sm font-semibold ${t.type === "credit" ? "text-emerald-300" : "text-rose-300"}`}>
                  {t.type === "credit" ? "+" : "−"}{inr(t.amount)}
                </span>
                <span className="col-span-2 text-xs text-neutral-400">{t.method}</span>
                <span className="col-span-3 truncate text-xs text-neutral-500" title={t.note}>{t.note}</span>
                <span className="col-span-1 font-mono text-xs text-neutral-400">{inr(t.balance_after)}</span>
                <span className="col-span-1 text-right font-mono text-[10px] text-neutral-600">{fmtDate(t.created_at)}</span>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
}
